import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { BadgeCheck } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { formatINR } from "@/lib/api";
import { Cta } from "./Cta";

const RotatingBadge = () => (
  <div className="pointer-events-none absolute -bottom-8 -left-3 z-20 h-24 w-24 sm:-left-8 sm:h-28 sm:w-28" aria-hidden>
    <div className="absolute inset-0 rounded-full bg-forest shadow-xl" />
    <svg viewBox="0 0 100 100" className="spin-slow absolute inset-0">
      <defs><path id="circ" d="M50,50 m-37,0 a37,37 0 1,1 74,0 a37,37 0 1,1 -74,0" /></defs>
      <text fill="#FAF8F3" fontSize="9.5" letterSpacing="2.6" fontFamily="Instrument Sans" fontWeight="600">
        <textPath href="#circ">15 HERBS · AYURVEDIC BLEND · </textPath>
      </text>
    </svg>
    <span className="absolute inset-0 grid place-items-center text-2xl font-extrabold text-gold">15</span>
  </div>
);

const ProductFrame = ({ product }) => {
  const { t } = useSite();
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const rotateX = useSpring(rx, { stiffness: 120, damping: 18 });
  const rotateY = useSpring(ry, { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 10);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 10);
  };
  const reset = () => { rx.set(0); ry.set(0); };
  return (
    <div ref={ref} className="relative mx-auto w-full max-w-[340px] sm:max-w-[440px] lg:max-w-[520px]" style={{ perspective: 1200 }} onMouseMove={onMove} onMouseLeave={reset}>
      <div className="absolute -inset-10 rounded-full bg-[radial-gradient(closest-side,rgba(201,162,39,0.35),transparent)] blur-2xl" aria-hidden />
      <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative">
        <div className="absolute -inset-3 rounded-[44px] border border-gold/60" aria-hidden />
        <div className="relative aspect-[1214/1295] overflow-hidden rounded-[36px] bg-sand shadow-[0_40px_80px_-30px_rgba(13,59,46,0.55)]">
          <img
            src={product.image_mobile || product.images?.[0]}
            srcSet={product.image_mobile ? `${product.image_mobile} 640w, ${product.images?.[0]} 1000w` : undefined}
            sizes="(min-width: 1024px) 520px, 88vw"
            alt={`${product.name} ${product.pack_size} jar with herbal ingredients`}
            width="1000" height="1067" fetchPriority="high" decoding="async"
            data-testid="hero-product-image"
            className="absolute inset-0 h-full w-full object-contain"
          />
        </div>
        <RotatingBadge />
        <div style={{ transform: "translateZ(40px)" }} className="absolute -right-2 bottom-10 z-20 rounded-2xl border border-white/60 bg-white/80 px-4 py-3 shadow-lg backdrop-blur-md sm:-right-6">
          <p className="text-sm font-semibold text-leaf">{t("pack")}</p>
          <p className="text-lg font-extrabold text-forest">{product.pack_size}</p>
        </div>
      </motion.div>
    </div>
  );
};

const PriceBlock = ({ product }) => {
  const { t } = useSite();
  const off = product.mrp > product.price ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;
  return (
    <div data-testid="hero-price" className="flex flex-wrap items-end gap-x-4 gap-y-2">
      <span className="t-price text-forest">{formatINR(product.price)}</span>
      {off > 0 && <span className="pb-1 text-lg font-medium text-sage line-through">{t("mrp")} {formatINR(product.mrp)}</span>}
      {off > 0 && <span className="mb-2 rounded-full bg-gold px-3 py-1 text-sm font-extrabold text-forest">{t("save_pct", { n: off })}</span>}
      <span className="w-full text-[15px] font-medium text-sage">{product.pack_size} · {t("incl_taxes")} · {product.payment_info?.split(/[.।]/)[0]}</span>
    </div>
  );
};

export const Hero = () => {
  const { content } = useSite();
  const p = content.product;
  const lines = p.headline_lines?.length ? p.headline_lines : [p.name];
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden bg-bone grain">
      <div className="absolute right-[-20%] top-[-30%] h-[70vw] w-[70vw] rounded-full bg-[radial-gradient(closest-side,rgba(47,107,79,0.12),transparent)]" aria-hidden />
      <div className="relative mx-auto grid max-w-7xl gap-x-16 gap-y-10 px-5 pb-16 pt-10 sm:px-8 lg:grid-cols-2 lg:grid-rows-[auto_1fr] lg:pb-24 lg:pt-16">
        <div>
          <p className="fade-up mb-5 inline-flex items-center gap-2 rounded-full border border-forest/15 bg-white/70 px-3.5 py-1.5 text-sm font-bold text-leaf">
            <span className="h-1.5 w-1.5 rounded-full bg-gold" />{p.name}
          </p>
          <h1 data-testid="hero-headline" className="t-hero text-forest">
            {lines.map((l, i) => (
              <span key={l} className="mask-line">
                <span style={{ animationDelay: `${0.08 + i * 0.12}s` }} className={i === 1 ? "text-leaf" : ""}>{l}</span>
              </span>
            ))}
          </h1>
        </div>

        <div className="fade-up lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-center" style={{ animationDelay: "0.2s" }}>
          <ProductFrame product={p} />
        </div>

        <div className="fade-up space-y-7" style={{ animationDelay: "0.45s" }}>
          <p data-testid="hero-subcopy" className="t-body max-w-xl text-sage">{p.subcopy}</p>
          {p.label_badges?.length > 0 && (
            <ul className="flex flex-wrap gap-2">
              {p.label_badges.map((b) => (
                <li key={b} className="inline-flex items-center gap-1.5 rounded-full bg-sand px-3.5 py-2 text-sm font-bold text-forest">
                  <BadgeCheck className="h-3.5 w-3.5 text-leaf" aria-hidden />{b}
                </li>
              ))}
            </ul>
          )}
          <PriceBlock product={p} />
          <div className="grid max-w-xl gap-3">
            <Cta kind="consult" name="hero_consultation" position="hero" className="w-full" />
            <Cta kind="call" name="hero_call" position="hero" variant="outline" className="w-full" />
          </div>
        </div>
      </div>
    </section>
  );
};
